(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-fandiego-configurator]';
  const GROUP_SELECTOR = '[data-fd-group]';
  const CONFIGURATION_MESSAGE =
    'Please complete all product configuration options before adding this fan to your cart.';

  class FanDiegoConfigurator {
    constructor(root) {
      this.root = root;
      this.form = root.closest('form');
      this.submitButton = this.form?.querySelector('[data-add-to-cart]');
      this.errorSummary = root.querySelector('[data-fd-error-summary]');
      this.errorList = root.querySelector('[data-fd-error-list]');
      this.status = root.querySelector('[data-fd-status]');
      this.variantIds = new Set(
        (root.dataset.fdVariantIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );
      this.availableVariantIds = new Set(
        (root.dataset.fdAvailableVariantIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );
      this.currentVariantId = root.dataset.fdCurrentVariantId || '';
      this.isSubmitting = false;
      this.hasAttemptedSubmit = false;

      if (!this.form || !this.submitButton || !this.errorSummary || !this.errorList) {
        return;
      }

      this.bindEvents();
      this.switchVariant(this.currentVariantId);
      this.validate({ showErrors: false });
      this.unlockSubmit();
      this.root.dataset.fdInitialized = 'true';
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event), true);

      this.root.addEventListener('change', (event) => {
        if (!event.target.matches('[data-fd-select]')) {
          return;
        }

        this.clearGroupError(event.target.closest(GROUP_SELECTOR));
        this.validate({ showErrors: this.hasAttemptedSubmit });
      });

      this.root.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-fd-choice]');
        if (choice && this.root.contains(choice) && !choice.disabled) {
          const group = choice.closest(GROUP_SELECTOR);
          group.querySelectorAll('[data-fd-choice]').forEach((button) => {
            button.setAttribute('aria-checked', button === choice ? 'true' : 'false');
          });
          this.clearGroupError(group);
          this.validate({ showErrors: this.hasAttemptedSubmit });
          return;
        }

        const previous = event.target.closest('[data-fd-tooltip-prev]');
        const next = event.target.closest('[data-fd-tooltip-next]');
        if (previous || next) {
          const slider = event.target.closest('[data-fd-tooltip-slider]');
          const images = slider?.querySelector('[data-fd-tooltip-images]');
          if (!images) {
            return;
          }

          const direction = previous ? -1 : 1;
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          images.scrollBy({
            left: direction * images.clientWidth,
            behavior: reduceMotion ? 'auto' : 'smooth'
          });
        }
      });
    }

    unlockSubmit() {
      if (!this.submitButton.hasAttribute('data-fd-configurator-submit-pending')) {
        return;
      }

      this.submitButton.removeAttribute('data-fd-configurator-submit-pending');
      if (this.availableVariantIds.has(this.currentVariantId)) {
        this.submitButton.disabled = false;
        this.submitButton.setAttribute('aria-disabled', 'false');
      }
    }

    handlesVariant(variantId) {
      return this.variantIds.has(String(variantId));
    }

    switchVariant(variantId) {
      const normalizedId = String(variantId || '');
      if (!this.handlesVariant(normalizedId)) {
        return;
      }

      this.currentVariantId = normalizedId;
      this.root.dataset.fdCurrentVariantId = normalizedId;

      this.root.querySelectorAll('[data-fd-scope="variant"]').forEach((group) => {
        const shouldShow = group.dataset.fdVariantId === normalizedId;

        if (!shouldShow) {
          this.resetGroup(group);
        }

        group.hidden = !shouldShow;
      });

      this.validate({ showErrors: this.hasAttemptedSubmit });
    }

    resetGroup(group) {
      const select = group.querySelector('[data-fd-select]');
      if (select) {
        select.value = '';
      }

      group.querySelectorAll('[data-fd-choice]').forEach((choice) => {
        choice.setAttribute('aria-checked', 'false');
      });

      this.clearGroupError(group);
    }

    visibleGroups() {
      return Array.from(this.root.querySelectorAll(GROUP_SELECTOR)).filter(
        (group) => !group.hidden
      );
    }

    selectedChoice(group) {
      const select = group.querySelector('[data-fd-select]');
      if (select) {
        return select.selectedIndex > 0 ? select.options[select.selectedIndex] : null;
      }

      return group.querySelector('[data-fd-choice][aria-checked="true"]');
    }

    validate({ showErrors, requireVariantSync = false }) {
      const errors = [];
      const visibleGroups = this.visibleGroups();
      const componentQuantities = new Map();

      this.root.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
        if (!group.hidden) {
          this.clearGroupError(group);
        }
      });

      if (visibleGroups.length === 0) {
        errors.push({
          message: 'This product has configurator data but no valid option groups for the selected motor.',
          target: this.root
        });
      }

      visibleGroups.forEach((group) => {
        const label = group.dataset.fdLabel || 'Configuration option';
        const selectableChoices = Array.from(
          group.querySelectorAll('[data-fd-choice], [data-fd-select] option:not(:first-child)')
        ).filter((choice) => !choice.disabled);

        if (selectableChoices.length === 0) {
          errors.push({
            message: `${label} has no available choices.`,
            target: group,
            group
          });
          return;
        }

        const choice = this.selectedChoice(group);
        if (!choice) {
          errors.push({
            message: `Choose an option for ${label}.`,
            target: group.querySelector('[data-fd-select], [data-fd-choice]') || group,
            group
          });
          return;
        }

        if (choice.disabled || choice.dataset.fdAvailable !== 'true') {
          errors.push({
            message: `The selected option for ${label} is unavailable.`,
            target: choice,
            group
          });
          return;
        }

        const quantityValue = choice.dataset.fdQuantity || '';
        if (!/^[1-9]\d*$/.test(quantityValue)) {
          errors.push({
            message: `${label} has an invalid component quantity.`,
            target: choice,
            group
          });
          return;
        }

        if (choice.dataset.fdEmpty === 'true') {
          if (choice.dataset.fdMappingPresent === 'true') {
            errors.push({
              message: `${label} references a component that is missing or invalid.`,
              target: choice,
              group
            });
          }
          return;
        }

        const componentIds = (choice.dataset.fdComponents || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        if (componentIds.length === 0 || componentIds.some((id) => !/^\d+$/.test(id))) {
          errors.push({
            message: `${label} has an invalid component selection.`,
            target: choice,
            group
          });
          return;
        }

        const quantity = Number.parseInt(quantityValue, 10);
        componentIds.forEach((componentId) => {
          const existingQuantity = componentQuantities.get(componentId) || 0;
          componentQuantities.set(componentId, existingQuantity + quantity);
        });
      });

      const baseVariantSelect = this.form.querySelector('[data-product-select]');
      const baseVariantId = baseVariantSelect?.value?.trim() || this.currentVariantId;

      if (!/^\d+$/.test(baseVariantId) || !this.handlesVariant(baseVariantId)) {
        errors.unshift({
          message: 'Choose an available Motor Finish.',
          target: baseVariantSelect || this.form
        });
      } else if (!this.availableVariantIds.has(baseVariantId)) {
        errors.unshift({
          message: 'The selected Motor Finish is unavailable.',
          target: baseVariantSelect || this.form
        });
      } else if (requireVariantSync && baseVariantId !== this.currentVariantId) {
        errors.unshift({
          message: 'The selected Motor Finish does not match the visible configuration options.',
          target: baseVariantSelect || this.form
        });
      }

      if (componentQuantities.has(baseVariantId)) {
        errors.push({
          message: 'A component is incorrectly mapped to the selected motor variant.',
          target: this.root
        });
      }

      if (componentQuantities.size === 0) {
        errors.push({
          message: 'The selected configuration does not contain any component products.',
          target: this.root
        });
      }

      if (showErrors) {
        this.renderErrors(errors);
      } else if (errors.length === 0) {
        this.clearErrors();
      }

      this.root.dataset.fdComplete = errors.length === 0 ? 'true' : 'false';

      return {
        valid: errors.length === 0,
        errors,
        baseVariantId,
        componentQuantities
      };
    }

    renderErrors(errors) {
      this.clearErrors();
      if (errors.length === 0) {
        return;
      }

      errors.forEach((error) => {
        const item = document.createElement('li');
        item.textContent = error.message;
        this.errorList.append(item);

        if (error.group) {
          error.group.classList.add('fd-configurator__group--invalid');
          const message = error.group.querySelector('[data-fd-group-error]');
          if (message) {
            message.textContent = error.message;
            message.hidden = false;
          }

          const control = error.group.querySelector('[data-fd-select], [data-fd-choice]');
          control?.setAttribute('aria-invalid', 'true');
        }
      });

      this.errorSummary.hidden = false;
      this.errorSummary.focus();
    }

    clearErrors() {
      this.errorList.replaceChildren();
      this.errorSummary.hidden = true;
      this.root.querySelectorAll(GROUP_SELECTOR).forEach((group) => this.clearGroupError(group));
    }

    clearGroupError(group) {
      if (!group) {
        return;
      }

      group.classList.remove('fd-configurator__group--invalid');
      group.querySelectorAll('[aria-invalid="true"]').forEach((control) => {
        control.removeAttribute('aria-invalid');
      });

      const message = group.querySelector('[data-fd-group-error]');
      if (message) {
        message.textContent = '';
        message.hidden = true;
      }
    }

    buildItems(validation) {
      const items = [{ id: validation.baseVariantId, quantity: 1 }];

      validation.componentQuantities.forEach((quantity, componentId) => {
        items.push({ id: componentId, quantity });
      });

      return items;
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isSubmitting) {
        return;
      }

      this.hasAttemptedSubmit = true;
      const validation = this.validate({ showErrors: true, requireVariantSync: true });
      if (!validation.valid) {
        this.setStatus(CONFIGURATION_MESSAGE);
        return;
      }

      const items = this.buildItems(validation);
      this.setSubmitting(true);
      this.clearErrors();
      this.setStatus('Adding the complete fan configuration to your cart...');

      try {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();
        if (!response.ok || result.status === 422) {
          const description =
            typeof result.description === 'string'
              ? result.description
              : result.message || 'Shopify could not add the complete configuration.';
          throw new Error(description);
        }

        this.setStatus('The complete fan configuration was added to your cart.');
        this.setSubmitting(false);

        const cartDrawerAvailable =
          window.theme?.settings?.cartType === 'drawer' &&
          document.querySelector('#CartDrawerForm');

        if (cartDrawerAvailable) {
          this.form.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: {
                product: result,
                addToCartBtn: this.submitButton
              },
              bubbles: true
            })
          );
        } else {
          window.location.assign(`${shopRoot}cart`);
        }
      } catch (error) {
        this.setSubmitting(false);
        this.showSubmissionError(error);
      }
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.submitButton.disabled = isSubmitting;
      this.submitButton.setAttribute('aria-disabled', isSubmitting ? 'true' : 'false');
      this.submitButton.classList.toggle('btn--loading', isSubmitting);
    }

    setStatus(message) {
      if (this.status) {
        this.status.textContent = message;
      }
    }

    showSubmissionError(error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'The complete configuration could not be added. Please try again.';

      this.clearErrors();
      const item = document.createElement('li');
      item.textContent = message;
      this.errorList.append(item);
      this.errorSummary.hidden = false;
      this.errorSummary.focus();
      this.setStatus('Nothing was added to your cart.');
    }
  }

  const initialize = (scope) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) {
      roots.push(scope);
    }
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      if (!root.fdConfiguratorInstance) {
        root.fdConfiguratorInstance = new FanDiegoConfigurator(root);
      }
    });
  };

  if (document.documentElement.dataset.fdConfiguratorBootstrapped !== 'true') {
    document.documentElement.dataset.fdConfiguratorBootstrapped = 'true';

    document.addEventListener('variant:change', (event) => {
      const variantId = event.detail?.variant?.id;
      if (!variantId) {
        return;
      }

      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        const instance = root.fdConfiguratorInstance;
        if (instance?.handlesVariant(variantId)) {
          instance.switchVariant(variantId);
        }
      });
    });

    document.addEventListener('shopify:section:load', (event) => {
      initialize(event.target);
    });
  }

  initialize(document);
})();
